import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PRWatchInfo, EpicSession } from '../../../../shared/types/epics';
import { StatusBadge, toneForPRWatchState, labelForPRWatchState } from '../ui/StatusBadge';
import type { StatusTone } from '../ui/StatusBadge';
import { Button } from '../ui/Button';
import { agentEmoji } from '../../lib/agentEmoji';
import { SessionRow, formatCost, formatTime, outcomeTone } from './AgentRunRow';
import { postPRReviewRefresh, postPRResolutionDecision } from '../../lib/api/epics';

/**
 * FORGE-302 — PR review tracking card. Renders Forge's live view of an
 * open PR's review-feedback loop: current state, fix round, whether
 * auto-fix is on for this repo/plan, and how much feedback is sitting in
 * the debounce window right now. Mirrors StuckDiagnosisCard's "null when
 * there's nothing to show" discipline — an epic with no open PR at all
 * renders nothing, so the card never takes up space or implies a feature
 * the repo isn't using.
 *
 * Also renders (in a lighter "not tracked yet" form) when the epic has an
 * open PR but no PRWATCH# row — either the repo enabled pr_tracking after
 * this PR already opened, or the very first "check for feedback" call
 * hasn't run yet. The manual-trigger button covers both: it seeds the row
 * on first use and re-checks GitHub directly on every use after that.
 */

function relativeTime(iso: string): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** Attempt-segment color, matching the story RetryClusters progress bar. */
function segClass(s: EpicSession): string {
  if (s.isRunning) return 'bg-blue-400 animate-pulse';
  const o = (s.outcome ?? '').toUpperCase();
  if (o.includes('FORCE_COMPLETE')) return 'bg-amber-400';
  if (o.includes('COMPLETE')) return 'bg-emerald-500';
  if (o.includes('CRASH') || o.includes('FAIL')) return 'bg-red-500';
  return 'bg-slate-300';
}

/** One agent's runs, oldest-first, with rollups — mirrors a story RetryCluster. */
interface PrRunCluster {
  agentType: string;
  runs: EpicSession[];
  costUsd: number;
  iterations: number;
  productiveIterations: number;
  running: boolean;
  last: EpicSession;
}
function clusterPrRuns(sessions: EpicSession[]): PrRunCluster[] {
  const byAgent = new Map<string, EpicSession[]>();
  for (const s of sessions) {
    const list = byAgent.get(s.agentType);
    if (list) list.push(s);
    else byAgent.set(s.agentType, [s]);
  }
  return [...byAgent.entries()].map(([agentType, runs]) => {
    const sorted = [...runs].sort((a, b) => (a.startedAt < b.startedAt ? -1 : a.startedAt > b.startedAt ? 1 : 0));
    return {
      agentType,
      runs: sorted,
      costUsd: sorted.reduce((n, s) => n + (s.costUsd ?? 0), 0),
      iterations: sorted.reduce((n, s) => n + (s.iterations ?? 0), 0),
      productiveIterations: sorted.reduce((n, s) => n + (s.productiveIterations ?? 0), 0),
      running: sorted.some((s) => s.isRunning),
      last: sorted[sorted.length - 1],
    };
  }).sort((a, b) => (a.last.startedAt < b.last.startedAt ? -1 : 1));
}

interface PRTrackingCardProps {
  prWatch?: PRWatchInfo | null;
  /** Epic's own pr_url (present once READY_FOR_REVIEW opens a PR, whether or not tracking is on). */
  prUrl?: string | null;
  epicId: number | string;
  /** Opens the shared TranscriptDrawer (read + md/json download) for one PR-tracking agent run. */
  onOpenTranscript?: (storyId: string, agentType: string, taskArn?: string | null) => void;
}

export const PRTrackingCard: React.FC<PRTrackingCardProps> = ({ prWatch, prUrl, epicId, onOpenTranscript }) => {
  const [openClusters, setOpenClusters] = useState<Set<string>>(() => new Set());
  const queryClient = useQueryClient();
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const mutation = useMutation({
    mutationFn: () => postPRReviewRefresh(epicId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['epic', String(epicId)] });
      if (data.prClosed) {
        setResult({ ok: true, message: 'This PR is already closed — nothing to check.' });
      } else if (data.newItemsQueued > 0) {
        setResult({
          ok: true,
          message:
            `Queued ${data.newItemsQueued} new item${data.newItemsQueued === 1 ? '' : 's'} — ` +
            `Forge will classify and act within ~${Math.round(data.nextSweepEtaSeconds / 60)} min.`,
        });
      } else {
        setResult({ ok: true, message: 'No new feedback since the last check.' });
      }
    },
    onError: (err: Error) => setResult({ ok: false, message: err.message }),
  });

  // FORGE-309: the human's Approve/Reject on a reviewed proposal.
  const resolutionMutation = useMutation({
    mutationFn: (decision: 'approve' | 'reject') =>
      postPRResolutionDecision(epicId, decision, {
        prNumber: prWatch?.prNumber ?? 0,
        repo: prWatch?.repo ?? undefined,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['epic', String(epicId)] });
      setResult({
        ok: true,
        message:
          data.action === 'rejected'
            ? 'Rejected — Forge escalated this PR for manual attention.'
            : data.action === 'approved_code_merged'
              ? 'Approved — the proposed change was applied to the PR; Forge is watching again.'
              : 'Approved — recommendation posted to the PR; Forge is watching again.',
      });
    },
    onError: (err: Error) => setResult({ ok: false, message: err.message }),
  });

  // No PR at all yet — nothing to show or check.
  if (!prWatch && !prUrl) return null;

  const isEscalated = prWatch?.state === 'PR_FIX_ESCALATED';
  const isResolutionReady = prWatch?.state === 'RESOLUTION_READY';
  const needsHuman = isEscalated || isResolutionReady;

  const refreshButton = (
    <Button
      variant="secondary"
      size="sm"
      loading={mutation.isPending}
      onClick={() => { setResult(null); mutation.mutate(); }}
      data-testid="pr-check-feedback"
    >
      Check for feedback now
    </Button>
  );

  if (!prWatch) {
    // Open PR, but no tracking row yet (repo not opted in when the PR opened,
    // or this is simply the first check). The button seeds the row.
    return (
      <section
        data-testid="pr-tracking-card"
        className="rounded-xl border border-slate-200 bg-white px-4 py-3 space-y-2"
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-slate-800">PR Review Tracking</h2>
          {refreshButton}
        </div>
        <a
          href={prUrl ?? undefined}
          target="_blank"
          rel="noreferrer"
          className="block text-xs font-medium text-primary-600 hover:underline truncate"
        >
          {prUrl}
        </a>
        <p className="text-2xs text-slate-500">
          Not tracked yet — check for feedback to have Forge start watching this PR.
        </p>
        {result && (
          <p className={`text-2xs ${result.ok ? 'text-success-700' : 'text-danger-700'}`} role="status">
            {result.message}
          </p>
        )}
      </section>
    );
  }

  return (
    <section
      data-testid="pr-tracking-card"
      className={`rounded-xl border px-4 py-3 space-y-2 ${
        needsHuman ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'
      }`}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-slate-800">PR Review Tracking</h2>
        <div className="flex items-center gap-2">
          <StatusBadge tone={toneForPRWatchState(prWatch.state)} dot>
            {labelForPRWatchState(prWatch.state)}
          </StatusBadge>
          {refreshButton}
        </div>
      </div>

      <a
        href={prWatch.prUrl}
        target="_blank"
        rel="noreferrer"
        className="block text-xs font-medium text-primary-600 hover:underline truncate"
      >
        #{prWatch.prNumber}{prWatch.prTitle ? `: ${prWatch.prTitle}` : ''}
      </a>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-2xs text-slate-500">
        <span>
          Fix round <span className="font-medium text-slate-700">{prWatch.fixRound}</span> of {prWatch.maxFixRounds}
        </span>
        <span>
          Auto-fix <span className="font-medium text-slate-700">{prWatch.autoFixEnabled ? 'on' : 'off'}</span>
        </span>
        {prWatch.pendingFeedbackCount > 0 && (
          <span className="font-medium text-accent-600">
            {prWatch.pendingFeedbackCount} item{prWatch.pendingFeedbackCount === 1 ? '' : 's'} pending
          </span>
        )}
        {prWatch.classifierCostUsd > 0 && (
          <span>Classifier cost ${prWatch.classifierCostUsd.toFixed(4)}</span>
        )}
        {prWatch.lastActivityAt && <span>Last activity {relativeTime(prWatch.lastActivityAt)}</span>}
      </div>

      {isEscalated && (
        <div className="text-2xs text-amber-800 space-y-1" data-testid="pr-escalation-reason">
          <p className="font-medium">
            {prWatch.escalationReason ?? 'Forge flagged this PR as needing human judgment.'}
          </p>
          <p>
            Forge paused auto-processing on this PR — a maintainer needs to weigh in (respond in the PR or push a commit) before tracking resumes.
            {prWatch.escalationReason == null && " (No stored reason — check the PR's own comments for what triggered this.)"}
          </p>
        </div>
      )}

      {isResolutionReady && prWatch.proposedResolution && (
        <div className="text-2xs text-amber-900 space-y-2" data-testid="pr-resolution-ready">
          <p className="font-medium text-amber-800">
            Forge drafted a reviewed resolution for this escalation — approve to apply it, or reject to handle it manually.
          </p>
          <div className="rounded-lg border border-amber-200 bg-white/70 px-3 py-2 space-y-1">
            <p className="text-slate-700">{prWatch.proposedResolution.recommendation}</p>
            {prWatch.proposedResolution.hasCodeChanges && (
              <p className="text-2xs text-slate-500">
                Includes a code change on <span className="font-mono">{prWatch.proposedResolution.proposalBranch}</span> — approving merges it onto the PR.
              </p>
            )}
            {prWatch.proposedResolution.reviewNotes && (
              <p className="text-2xs text-slate-500 italic">Reviewer: {prWatch.proposedResolution.reviewNotes}</p>
            )}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button
              variant="primary"
              size="sm"
              loading={resolutionMutation.isPending && resolutionMutation.variables === 'approve'}
              disabled={resolutionMutation.isPending}
              onClick={() => { setResult(null); resolutionMutation.mutate('approve'); }}
              data-testid="pr-resolution-approve"
            >
              Approve
            </Button>
            <Button
              variant="secondary"
              size="sm"
              loading={resolutionMutation.isPending && resolutionMutation.variables === 'reject'}
              disabled={resolutionMutation.isPending}
              onClick={() => { setResult(null); resolutionMutation.mutate('reject'); }}
              data-testid="pr-resolution-reject"
            >
              Reject
            </Button>
          </div>
        </div>
      )}

      {prWatch.sessions && prWatch.sessions.length > 0 && (
        <div className="pt-1" data-testid="pr-agent-runs">
          <div className="px-1 text-2xs font-medium text-slate-500 mb-1">Agent runs</div>
          {clusterPrRuns(prWatch.sessions).map((c) => {
            const isOpen = openClusters.has(c.agentType);
            const badge: { tone: StatusTone; label: string } = c.running
              ? { tone: 'inprogress', label: 'running' }
              : { tone: outcomeTone(c.last.outcome), label: c.runs.length > 1 ? `${c.runs.length} runs` : (c.last.outcome ?? 'finished') };
            return (
              <div key={c.agentType} className="border-t border-slate-100 first:border-t-0">
                <button
                  type="button"
                  onClick={() => setOpenClusters((prev) => {
                    const next = new Set(prev);
                    if (next.has(c.agentType)) next.delete(c.agentType); else next.add(c.agentType);
                    return next;
                  })}
                  aria-expanded={isOpen}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="w-3 shrink-0 text-xs text-slate-400">{isOpen ? '▾' : '▸'}</span>
                  <span className="w-28 shrink-0 truncate font-mono text-xs text-slate-900">
                    <span aria-hidden="true">{agentEmoji(c.agentType)}</span> {c.agentType}
                  </span>
                  <span className="flex min-w-[56px] flex-1 items-center gap-[3px]" aria-hidden="true">
                    {c.runs.map((s, i) => (
                      <span
                        key={s.sessionId + i}
                        title={`run ${i + 1} · ${formatTime(s.startedAt)}`}
                        className={`h-2.5 rounded-sm ${segClass(s)}`}
                        style={{ flexGrow: Math.max(1, Math.round((s.durationMs ?? 0) / 30_000)), flexBasis: 0, minWidth: '6px' }}
                      />
                    ))}
                  </span>
                  <StatusBadge tone={badge.tone} dot={c.running}>{badge.label}</StatusBadge>
                  <span className="w-16 shrink-0 text-right font-mono text-xs tabular-nums text-slate-600">{formatCost(c.costUsd)}</span>
                  <span className="hidden w-14 shrink-0 text-right text-xs tabular-nums text-slate-500 sm:inline">
                    {c.iterations > 0 ? `${c.productiveIterations}/${c.iterations} it` : '—'}
                  </span>
                </button>
                {isOpen && (
                  <div className="bg-slate-50/60 py-1.5 pl-4 pr-2">
                    <div className="overflow-x-auto border-l-2 border-slate-200 pl-2">
                      <table className="w-full text-xs">
                        <tbody>
                          {c.runs.map((s) => (
                            <SessionRow
                              key={s.sessionId}
                              session={s}
                              showAgent={false}
                              onOpenTranscript={(agent, taskArn) => onOpenTranscript?.(s.storyId, agent, taskArn)}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {result && (
        <p className={`text-2xs ${result.ok ? 'text-success-700' : 'text-danger-700'}`} role="status">
          {result.message}
        </p>
      )}
    </section>
  );
};

export default PRTrackingCard;
